import { useEffect, useState, useCallback } from "react";

type Listener = () => void;

// Date range filters
let appliedColumnFilters: Record<string, { from: string; to: string }> = {};
const dateListeners = new Set<Listener>();

// Country name filters (from google analytics)
let appliedColumnCountryNameFilters: Record<string, any[]> = {};
const countryNameListeners = new Set<Listener>();

// Country id filters (from backend)
let appliedColumnCountryIdFilters: Record<string, any[]> = {};
const countryIdListeners = new Set<Listener>();

// Date range filters
export const getAppliedColumnFilters = () => appliedColumnFilters;

export const useAppliedColumnFilters = () => {
  const [filters, setFilters] = useState(() => getAppliedColumnFilters());

  useEffect(() => {
    setFilters(getAppliedColumnFilters());

    const unsubscribe = subscribeDateFilters(() => {
      setFilters(getAppliedColumnFilters());
    });

    return () => {
      unsubscribe();
    };
  }, []);

  const subscribeDateFilters = (listener: Listener) => {
    dateListeners.add(listener);
    return () => {
      dateListeners.delete(listener);
    };
  };

  const setter = useCallback(
    (
      value:
        | Record<string, { from: string; to: string }>
        | ((
            prev: Record<string, { from: string; to: string }>
          ) => Record<string, { from: string; to: string }>)
    ) => {
      const newFilters =
        typeof value === "function" ? value(getAppliedColumnFilters()) : value;
      setAppliedColumnFilters(newFilters);
    },
    []
  );

  return [filters, setter] as const;
};

// Country name filters
export const getAppliedColumnCountryNameFilters = () =>
  appliedColumnCountryNameFilters;

const subscribeCountryNameFilters = (listener: Listener) => {
  countryNameListeners.add(listener);
  return () => {
    countryNameListeners.delete(listener);
  };
};

export const useAppliedColumnCountryNameFilters = () => {
  const [filters, setFilters] = useState(() =>
    getAppliedColumnCountryNameFilters()
  );

  useEffect(() => {
    setFilters(getAppliedColumnCountryNameFilters());

    const unsubscribe = subscribeCountryNameFilters(() => {
      setFilters(getAppliedColumnCountryNameFilters());
    });

    return () => {
      unsubscribe();
    };
  }, []);

  const setter = useCallback(
    (
      value:
        | Record<string, any[]>
        | ((prev: Record<string, any[]>) => Record<string, any[]>)
    ) => {
      const newFilters =
        typeof value === "function"
          ? value(getAppliedColumnCountryNameFilters())
          : value;
      setAppliedColumnCountryNameFilters(newFilters);
    },
    []
  );

  return [filters, setter] as const;
};

// Country id filters
export const getAppliedColumnCountryIdFilters = () =>
  appliedColumnCountryIdFilters;

const subscribeCountryIdFilters = (listener: Listener) => {
  countryIdListeners.add(listener);
  return () => {
    countryIdListeners.delete(listener);
  };
};

export const useAppliedColumnCountryIdFilters = () => {
  const [filters, setFilters] = useState(() =>
    getAppliedColumnCountryIdFilters()
  );

  useEffect(() => {
    setFilters(getAppliedColumnCountryIdFilters());

    const unsubscribe = subscribeCountryIdFilters(() => {
      setFilters(getAppliedColumnCountryIdFilters());
    });

    return () => {
      unsubscribe();
    };
  }, []);

  const setter = useCallback(
    (
      value:
        | Record<string, any[]>
        | ((prev: Record<string, any[]>) => Record<string, any[]>)
    ) => {
      const newFilters =
        typeof value === "function"
          ? value(getAppliedColumnCountryIdFilters())
          : value;
      setAppliedColumnCountryIdFilters(newFilters);
    },
    []
  );

  return [filters, setter] as const;
};

export const setAppliedColumnFilters = (
  filters: Record<string, { from: string; to: string }>
) => {
  appliedColumnFilters = filters;
  dateListeners.forEach((listener) => listener());
};

export const setAppliedColumnCountryNameFilters = (
  filters: Record<string, any[]>
) => {
  appliedColumnCountryNameFilters = filters;
  countryNameListeners.forEach((listener) => listener());
};

export const setAppliedColumnCountryIdFilters = (
  filters: Record<string, any[]>
) => {
  appliedColumnCountryIdFilters = filters;
  countryIdListeners.forEach((listener) => listener());
};
